import { flexRender, type Table as TsTable } from '@tanstack/react-table';
import { twJoin, twMerge } from 'tailwind-merge';
import { SortIcon } from './SortIcon';
import {
  SkeletonRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeadRow,
  TableRow,
} from './TableCore';

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={100}>
        <div className="flex size-full min-h-16 flex-col justify-center gap-3 py-4 sm:flex-row sm:gap-2 lg:items-center">
          {children}
        </div>
      </TableCell>
    </TableRow>
  );
}

function LoadingSkeleton({
  count,
  columnCount,
  hasActions,
  skeletonRowClassName,
}: {
  count: number;
  columnCount: number;
  hasActions: boolean;
  skeletonRowClassName?: string;
}) {
  const actionColumnIndex = hasActions ? columnCount - 1 : undefined;

  return (
    <>
      {Array.from({ length: count }).map((_, rowIndex) => (
        <SkeletonRow
          skeletonRowClassName={skeletonRowClassName}
          columnCount={columnCount}
          actionColumnIndex={actionColumnIndex}
          key={`skeleton-row-${rowIndex}`}
        />
      ))}
    </>
  );
}

function TableHeaderContent<T>({
  table,
  tableHeaderClassname,
}: {
  table: TsTable<T>;
  tableHeaderClassname?: string;
}) {
  return (
    <TableHeader className={tableHeaderClassname}>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableHeadRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const align = header.column.columnDef.meta?.align || 'left';

            const sortIcon = (() => {
              if (!header.column.getCanSort()) {
                return null;
              }

              const sortDirection = header.column.getIsSorted() || undefined;
              return (
                <SortIcon
                  className={twJoin(
                    'group-hover:visible group-focus:visible',
                    sortDirection ? 'visible' : 'invisible'
                  )}
                  sortDirection={sortDirection}
                />
              );
            })();

            const getSortTitle = () => {
              if (!header.column.getCanSort()) {
                return undefined;
              }
              return header.column.getNextSortingOrder() === 'asc'
                ? 'Click to sort ascending'
                : header.column.getNextSortingOrder() === 'desc'
                  ? 'Click to sort descending'
                  : 'Click to clear sort';
            };

            return (
              <TableHead
                key={header.id}
                colSpan={header.colSpan}
                className={twMerge(
                  align === 'right' ? 'text-right' : 'text-left',
                  header.column.columnDef.meta?.cellHeaderContainerClassName
                )}
              >
                {header.isPlaceholder ? null : (
                  <div
                    className={twMerge(
                      'group flex items-center gap-1 text-contentSecondary',
                      align === 'right' ? 'justify-end' : '',
                      header.column.columnDef.meta?.cellHeaderClassname,
                      header.column.getCanSort()
                        ? 'cursor-pointer select-none hover:brightness-125 focus:brightness-125 active:brightness-125'
                        : ''
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && header.column.getCanSort()) {
                        header.column.getToggleSortingHandler()?.(e);
                      }
                    }}
                    tabIndex={header.column.getCanSort() ? 0 : undefined}
                    title={getSortTitle()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {sortIcon}
                  </div>
                )}
              </TableHead>
            );
          })}
        </TableHeadRow>
      ))}
    </TableHeader>
  );
}

type DesktopTableWrapperProps<T> = {
  table: TsTable<T>;
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  tableHeaderClassname?: string;
  ref?: React.RefObject<HTMLDivElement | null>;
};

function DesktopTableWrapper<T>({
  ref,
  table,
  className,
  style,
  tableHeaderClassname,
  children,
}: DesktopTableWrapperProps<T>) {
  return (
    <Table ref={ref} className={className} style={style}>
      <TableHeaderContent table={table} tableHeaderClassname={tableHeaderClassname} />
      <TableBody>{children}</TableBody>
    </Table>
  );
}

type DesktopTableProps<T> = {
  table: TsTable<T>;
  isError?: boolean;
  isPending: boolean;
  className?: string;
  noDataText?: string;
  isConnected: boolean;
  isFetching?: boolean;
  skeletonRows?: number;
  onRowClick?: (row: T) => void;
  tableHeaderClassname?: string;
  skeletonRowClassName?: string;
  requireConnected?: boolean;
  hasActions?: boolean;
  style?: React.CSSProperties;
  ref?: React.RefObject<HTMLDivElement | null>;
};

export const DesktopTable = <T,>({
  ref,
  table,
  style,
  isError,
  className,
  isPending,
  isFetching,
  noDataText,
  onRowClick,
  isConnected,
  skeletonRows = 4,
  tableHeaderClassname,
  skeletonRowClassName,
  requireConnected = true,
  hasActions = false,
}: DesktopTableProps<T>) => {
  const rows = table.getRowModel().rows;
  const columns = table.getAllColumns();

  if (!isConnected && requireConnected) {
    return (
      <DesktopTableWrapper
        ref={ref}
        style={style}
        table={table}
        className={className}
        tableHeaderClassname={tableHeaderClassname}
      >
        <EmptyState>
          <p className="text-contentSecondary">
            Connect your wallet to deposit funds & start trading
          </p>
        </EmptyState>
      </DesktopTableWrapper>
    );
  }

  if (isPending && isFetching) {
    return (
      <DesktopTableWrapper
        ref={ref}
        style={style}
        table={table}
        className={className}
        tableHeaderClassname={tableHeaderClassname}
      >
        <LoadingSkeleton
          count={skeletonRows}
          columnCount={columns.length}
          hasActions={hasActions}
          skeletonRowClassName={skeletonRowClassName}
        />
      </DesktopTableWrapper>
    );
  }

  if (rows.length === 0) {
    return (
      <DesktopTableWrapper
        ref={ref}
        style={style}
        table={table}
        className={className}
        tableHeaderClassname={tableHeaderClassname}
      >
        <EmptyState>
          {isError ? (
            <>
              <p className="text-contentTertiary">Failed to fetch table.</p>
              <p className="text-contentSecondary">Try reloading the page.</p>
            </>
          ) : (
            <p className="text-contentSecondary">{noDataText}</p>
          )}
        </EmptyState>
      </DesktopTableWrapper>
    );
  }

  return (
    <DesktopTableWrapper
      ref={ref}
      style={style}
      table={table}
      className={className}
      tableHeaderClassname={tableHeaderClassname}
    >
      {rows.map((row) => (
        <TableRow
          className={table.options.meta?.getRowClassName?.(row)}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
          key={row.id}
        >
          {row.getVisibleCells().map((cell) => {
            const align = cell.column.columnDef.meta?.align || 'left';
            return (
              <TableCell
                key={cell.id}
                className={twMerge(
                  'font-normal text-contentTertiary',
                  align === 'right' ? 'text-right' : 'text-left',
                  cell.column.columnDef.meta?.cellContainerClassName
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </DesktopTableWrapper>
  );
};
